import { BotClickFilter } from "@redotech/redo-marketing-common/bot-click-filter";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo } from "react";

const botFilterOptions: TextRedoListItem<BotFilter>[] = [
  { id: "all", type: "text", text: "All clicks", value: "all" },
  {
    id: "human",
    type: "text",
    text: "Human clicks",
    value: BotClickFilter.HUMAN,
  },
  { id: "bot", type: "text", text: "Bot clicks", value: BotClickFilter.BOT },
];

export const MarketingBotFilter = memo(function MarketingBotFilter({
  botFilter,
  setBotFilter,
}: {
  botFilter: BotFilter;
  setBotFilter: (value: BotFilter) => void;
}) {
  const selectedOption = botFilterOptions.find(
    (option) => option.value === botFilter,
  );

  return (
    <RedoSingleSelectDropdownInput<BotFilter>
      fitToAnchor={false}
      flexGrow={false}
      label="Clicks"
      options={botFilterOptions}
      optionSelected={({ item }) => setBotFilter(item.value)}
      selectedItem={selectedOption}
      size="xs"
    />
  );
});

export type BotFilter = "all" | BotClickFilter;

export function getClickBotFilter(
  botFilter: BotFilter,
): BotClickFilter | undefined {
  return botFilter === "all" ? undefined : botFilter;
}

/** Names the basis of a click measure, so a filtered figure never renders under
 *  the unfiltered one's label. Undefined when every click counts. */
export function clickBasisLabel(
  botFilter: BotFilter,
): "Bot" | "Human" | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (botFilter) {
    case BotClickFilter.BOT:
      return "Bot";
    case BotClickFilter.HUMAN:
      return "Human";
    default:
      return undefined;
  }
}

/** Why a filtered click measure is not on the same basis as the rest of the
 *  page. */
export function clickBasisExplanation(
  botFilter: BotFilter,
): string[] | undefined {
  const basis = clickBasisLabel(botFilter);
  if (!basis) {
    return undefined;
  }
  const kind = basis === "Bot" ? "bots" : "humans";
  return [
    `Clicks classified as coming from ${kind}, over every message sent. A send carries no bot status, so the denominator is never filtered.`,
    "Clicks the classifier never evaluated count as neither human nor bot, so the two do not add up to All clicks.",
  ];
}
